import React from 'react';
import PropTypes from 'prop-types';

const Stepper = ({
  steps,
  activeStep,
  completedSteps = [],
  onStepClick,
  showProgressBar = true,
  variant = 'default',
  className = '',
  stepLabel,
  completeLabel,
}) => {
  const isCompleted = (index) => completedSteps.includes(index);
  const isActive = (index) => index === activeStep;

  return (
    <div className={className}>
      {/* Progress Bar */}
      {showProgressBar && (
        <div className="mb-6">
          <div className="flex justify-between text-xs text-[var(--succession-text-muted)] mb-2">
            <span>{stepLabel || `Step ${activeStep + 1} of ${steps.length}`}</span>
            <span>{completeLabel || `${Math.round(((activeStep + 1) / steps.length) * 100)}% Complete`}</span>
          </div>
          <div className="w-full bg-[var(--succession-bg-tertiary)] rounded-full h-2">
            <div 
              className="h-2 rounded-full transition-all duration-500 ease-out"
              style={{ 
                width: `${((activeStep + 1) / steps.length) * 100}%`,
                background: 'var(--succession-primary)'
              }}
            />
          </div>
        </div>
      )}

      {/* Steps */}
      <div className="flex items-start justify-center w-full pb-2 px-2">
        <div className="flex items-start justify-between" style={{ width: '100%', maxWidth: '100%' }}>
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              {/* Step Container */}
              <div className="flex flex-col items-center" style={{ flex: '1 1 0', minWidth: 0, maxWidth: `${100 / steps.length}%` }}>
              <div 
                className={`
                  relative w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold cursor-pointer mb-2 flex-shrink-0
                  transition-all duration-300 ease-in-out transform hover:scale-105
                  ${isActive(index)
                    ? 'text-white shadow-lg ring-4' 
                    : isCompleted(index)
                      ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-md' 
                      : 'bg-[var(--succession-bg-primary)] border-2 border-[var(--succession-border)] text-[var(--succession-text-muted)] hover:border-[var(--succession-border-hover)]'
                  }
                `}
                style={isActive(index) ? {
                  background: 'var(--succession-primary)',
                  '--tw-ring-color': 'var(--succession-primary)',
                  '--tw-ring-opacity': '0.2'
                } : {}}
                onClick={() => onStepClick && onStepClick(index)}
              >
                  {isCompleted(index) ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <span>{index + 1}</span>
                  )}
                  
                {/* Pulse animation for active step */}
                {isActive(index) && (
                  <div className="absolute inset-0 rounded-full opacity-75 animate-ping" style={{ backgroundColor: 'var(--succession-primary)' }} />
                )}
                </div>
                
                {variant === 'default' && (
                  <div 
                    className={`text-[10px] font-medium cursor-pointer text-center transition-colors duration-200 break-words leading-tight ${
                      isActive(index) ? 'font-semibold' : 
                      isCompleted(index) ? 'text-green-600' : 'text-[var(--succession-text-muted)]'
                    }`}
                    style={{ 
                      width: '100%', 
                      wordWrap: 'break-word', 
                      overflowWrap: 'break-word', 
                      minWidth: 0, 
                      fontSize: '10px',
                      ...(isActive(index) ? { color: 'var(--succession-primary)' } : {})
                    }}
                    onClick={() => onStepClick && onStepClick(index)}
                  >
                    {step.label}
                  </div>
                )}
              </div>
              
              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className={`h-0.5 mt-5 mx-1 transition-colors duration-300 flex-shrink ${
                  isCompleted(index) ? 'bg-green-400' : 'bg-[var(--succession-border)]'
                }`} style={{ width: '30px', minWidth: '20px' }} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

Stepper.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
  activeStep: PropTypes.number.isRequired,
  completedSteps: PropTypes.arrayOf(PropTypes.number),
  onStepClick: PropTypes.func,
  showProgressBar: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'compact']),
  className: PropTypes.string,
  stepLabel: PropTypes.string,
  completeLabel: PropTypes.string,
};

export default Stepper;

